/**
 * 视图滚动动画
 * 使用方法：
 * 1. 多元素
 * <div ro-scrollspy="target: .scroll-inview-item; y: 30;">
 *   <div class="scroll-inview-item">Item 1</div>
 *   <div class="scroll-inview-item">Item 2</div>
 *   <div class="scroll-inview-item">Item 3</div>
 * </div>
 *  参数：
 * target: 目标元素
 * x: 水平偏移，默认为0
 * y: 垂直偏移，默认为0
 * duration: 动画持续时间，默认为0.6
 * delay: 每个元素的延迟时间，默认为0.15
 * ease: 缓动函数，默认为''
 *
 * 2. 单独元素
 * <div ro-scroll-inview="y: 30;">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 *   <div>Item 4</div>
 * </div>
 * 参数：
 * x: 水平偏移，默认为0
 * y: 垂直偏移，默认为0
 * duration: 动画持续时间，默认为0.6
 * delay: 每个元素的延迟时间，默认为0.15
 * ease: 缓动函数，默认为''
 */
class ScrollAnimations {
  constructor() {
    this.init();
  }

  init() {
    // 处理多元素动画
    document.querySelectorAll("[ro-scrollspy]").forEach((container) => {
      const config = this.parseConfig(container.getAttribute("ro-scrollspy"));
      const targets = container.querySelectorAll(config.target);

      const originOverflow = container.style.overflow;
      container.style.overflow = "initial";

      // 初始化隐藏所有目标元素
      Array.from(targets).forEach((element) => {
        // 保存原始过渡效果
        const originalTransition = element.style.transition;
        const animationTransition = `opacity ${config.duration}s, transform ${config.duration}s ${config.ease}`;

        // 合并过渡效果，确保不覆盖原有效果
        const combinedTransition =
          originalTransition && originalTransition !== "none"
            ? `${originalTransition}, ${animationTransition}`
            : animationTransition;

        element.style.opacity = "0";
        element.style.transform = `translate(${config.x}px, ${config.y}px)`;
        element.style.transition = combinedTransition;
      });

      // 创建观察器监控容器
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // 当容器可见时，依次显示所有元素
              Array.from(targets).forEach((element, index) => {
                setTimeout(
                  () => {
                    element.style.opacity = config.opacity;
                    element.style.transform = "translate(0, 0)";

                    // 最后一个元素动画完成后恢复原始overflow
                    if (index === targets.length - 1) {
                      setTimeout(() => {
                        container.style.overflow = originOverflow;
                      }, config.duration * 1000);
                    }
                  },
                  index * config.delay * 1000,
                );
              });

              // 一旦触发，取消观察
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "0px 0px -10% 0px",
        },
      );

      observer.observe(container);
    });

    // 处理单独元素动画
    document.querySelectorAll("[ro-scroll-inview]").forEach((element) => {
      const config = this.parseConfig(element.getAttribute("ro-scroll-inview"));

      // 保存原始过渡效果
      const originalTransition = element.style.transition;
      const animationTransition = `opacity ${config.duration}s, transform ${config.duration}s ${config.ease}`;

      // 合并过渡效果，确保不覆盖原有效果
      const combinedTransition =
        originalTransition && originalTransition !== "none"
          ? `${originalTransition}, ${animationTransition}`
          : animationTransition;

      // 初始化隐藏元素
      element.style.opacity = "0";
      element.style.transform = `translate(${config.x}px, ${config.y}px)`;
      element.style.transition = combinedTransition;

      // 创建观察器监控单个元素
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              element.style.opacity = config.opacity;
              element.style.transform = "translate(0, 0)";

              // 一旦触发，取消观察
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.01,
          rootMargin: "0px 0px -1% 0px",
        },
      );

      observer.observe(element);
    });
  }

  // 解析配置
  parseConfig(attr) {
    // 默认配置
    const config = {
      target: ".scroll-inview-item",
      opacity: 1, // 目标透明度
      x: 0, // 初始 X 偏移
      y: 0, // 初始 Y 偏移
      duration: 0.6, // 动画持续时间
      delay: 0.15, // 每个元素的延迟时间
      ease: "", // 缓动函数
    };
    if (!attr) return config;
    // 解析属性字符串
    attr.split(";").forEach((item) => {
      const [key, value] = item.split(":").map((str) => str.trim());
      if (key && value) {
        // 转换数值类型的参数
        if (["x", "y", "duration", "delay"].includes(key)) {
          config[key] = parseFloat(value);
        } else {
          config[key] = value;
        }
      }
    });
    return config;
  }
}

// 初始化命名空间
(function () {
  window.redodo = {
    debounce,
    throttle,
    showLoading,
    hideLoading,
    disableScroll,
    enableScroll,
    openChatApp,
  };
})();

// 添加禁用滚动条的方法
function disableScroll() {
  const scrollbarWidth = getScrollbarWidth();
  document.body.style.overflow = "hidden";
  document.body.style.paddingRight = `${scrollbarWidth}px`;

  // 处理固定定位的元素
  document.querySelectorAll(".fixed-header, .fixed-element").forEach((el) => {
    el.style.paddingRight = `${scrollbarWidth}px`;
  });
}

// 添加获取滚动条宽度的函数
function getScrollbarWidth() {
  const outer = document.createElement("div");
  outer.style.visibility = "hidden";
  outer.style.overflow = "scroll";
  document.body.appendChild(outer);

  const inner = document.createElement("div");
  outer.appendChild(inner);

  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
  outer.parentNode.removeChild(outer);

  return scrollbarWidth;
}

// 添加恢复滚动条的方法
function enableScroll() {
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";

  // 处理固定定位的元素
  document.querySelectorAll(".fixed-header, .fixed-element").forEach((el) => {
    el.style.paddingRight = "";
  });
}

document.addEventListener("DOMContentLoaded", function () {
  new ScrollAnimations();
});

(function () {
  // 转义选择器中的特殊字符（兼容ID选择器）
  const escapeSelector = (selector) => {
    return selector.replace(/([.#:[\]\\])/g, "\\$1");
  };

  document.body.addEventListener("click", function (event) {
    const target = event.target.closest('a[href^="#"]');
    if (!target) return;

    event.preventDefault();

    let originalHref = target.getAttribute("href") || "";

    // 统一处理空href
    if (!originalHref || originalHref === "#") {
      target.setAttribute("href", "javascript:;");
      return;
    }

    // 提取并验证 hash
    const hashMatch = originalHref.match(/#(.+)/);
    if (!hashMatch) {
      target.setAttribute("href", "javascript:;");
      return;
    }

    const rawId = hashMatch[1];
    const targetId = escapeSelector(rawId);
    const element = document.getElementById(rawId);

    // 目标不存在处理
    if (!element) {
      console.warn(`Anchor target #${targetId} not found`);
      target.setAttribute("href", "javascript:;");
      return;
    }

    // 获取 header 高度
    const header = document.querySelector("header");
    const headerHeight = header ? header.offsetHeight : 0;

    // 获取 data-offset，默认值为 0
    const offsetAttr = target.getAttribute("data-offset") || "0";
    const offset = parseInt(offsetAttr, 10) || 0;

    const scrollPosition = element.offsetTop - headerHeight - offset;
    console.log("offset", offset);
    console.log("scrollPosition", scrollPosition);

    try {
      if ("scrollBehavior" in document.documentElement.style) {
        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      } else {
        // fallback，不建议动画实现（无jQuery），这里保留简单替代
        window.scrollTo(0, scrollPosition);
      }
    } catch (error) {
      console.error("Scroll error:", error);
    }

    // 焦点管理
    if (document.activeElement !== element) {
      element.setAttribute("tabindex", "-1");
      element.focus({ preventScroll: true });
    }
  });
})();

Shopify.formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};
if (!window.redodo) window.redodo = {};
window.redodo.addCartCount = 0;
window.redodo.initCart = function (sections) {
  console.log('sections==============', sections);
  if (sections) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sections, "text/html");
    const oldCart = document.querySelector("ro-cart-drawer");
    const newCart = doc.querySelector("ro-cart-drawer");
    if (newCart) {
      document.querySelector(".ro-header__cart_count").textContent =
        newCart.dataset.roCartCount;

      if (oldCart.dataset.open === "true") {
        newCart.dataset.open = "true";
        oldCart.replaceWith(newCart);
      } else {
        oldCart.replaceWith(newCart);
        setTimeout(() => {
          newCart.open();
        }, 100);
      }
    }
  }
};
// 同步接口版本 - 使用 Promise 包装
window.redodo.addCart = function (items) {
  return new Promise((resolve, reject) => {
    // 每次调用都进行计数，将计数存储在window.redodo.addCartCount中
    window.redodo.addCartCount++;
    let sections = "ro-cart-drawer,ro-cart";
    if (window.location.pathname === "/cart") {
      sections = "ro-cart";
    } else {
      sections = "ro-cart-drawer";
    }
    let formData = {
      items: items,
      sections: sections,
    };

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status && response.status !== 200) {
          window.redodo.showToast("error", response.message);
          reject(new Error(response.message));
          return;
        }
        if (window.location.pathname === "/cart") {
          window.redodo.initCartMain(response.sections?.["ro-cart"]);
        } else {
          window.redodo.initCart(response.sections?.["ro-cart-drawer"]);
        }
        // if (window.Shopify.onItemAdded) {
        //   window.Shopify.onItemAdded(response.items);
        // }
        resolve(response);
      })
      .catch((error) => {
        console.error("Error:", error);
        reject(error);
      });
  });
};

window.redodo.initCartMain = function (sections) {
  if (sections) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sections, "text/html");
    const oldCart = document.querySelector("ro-cart");
    const newCart = doc.querySelector("ro-cart");
    if (newCart) {
      document.querySelector(".ro-header__cart_count").textContent =
        newCart.dataset.roCartCount;
      oldCart.replaceWith(newCart);
    }
  }
};
window.redodo.refreshCart = function (number) {
  fetch(window.Shopify.routes.root + "?sections=ro-cart-drawer", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((response) => response.json())
    .then((response) => {
      window.redodo.initCart(response?.["ro-cart-drawer"]);
      window.redodo.addCartCount = number;
    })
    .catch((error) => {
      console.error("Error:", error);
    });
};

// 在全局点击事件监听中添加
document.addEventListener("click", async (e) => {
  const buyNowBtn = e.target.closest(".buy-now-btn-card-button");
  if (!buyNowBtn) return;
  const variantId = buyNowBtn.dataset.first_variant_id;
  if (!variantId) return;
  $(buyNowBtn).find(".rotate_icon").removeClass("hidden").addClass("flex");
  $(buyNowBtn)
    .find(".loading-button-text")
    .addClass("hidden")
    .removeClass("flex");
  window.location.href = `/cart/${variantId}:1`;
});
window.addEventListener("pageshow", function (event) {
  if (event.persisted) {
    window.location.reload();
  }
});

// 防抖函数
function debounce(fn, delay = 300) {
  let timer = null;

  return function (...args) {
    if (timer) clearTimeout(timer);

    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

// 节流函数
function throttle(fn, delay = 300) {
  let timer = null;

  return function (...args) {
    if (timer) return;

    timer = setTimeout(() => {
      fn.apply(this, args);
      timer = null;
    }, delay);
  };
}

function showLoading(isDisableScroll = true) {
  const globalLoading = document.querySelector(".global-loading_wrapper");
  globalLoading.style.display = "flex";
  globalLoading.classList.remove("hidden");
  if (isDisableScroll) {
    window.redodo.disableScroll();
  }
}

function hideLoading(isEnableScroll = true) {
  const globalLoading = document.querySelector(".global-loading_wrapper");
  globalLoading.classList.add("hidden");
  if (isEnableScroll) {
    window.redodo.enableScroll();
  }
}

function openChatApp() {
  const nextopChatTriggerEle = document.querySelector("#nextop-chat");
  const chatwootTriggerEle = document.querySelector(".woot-widget-bubble");
  if (chatwootTriggerEle) {
    chatwootTriggerEle.click();
  } else if (nextopChatTriggerEle) {
    NextopLiveChat("openChat");
  }
}
